import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface AnuncioData {
  activo: boolean;
  texto: string;
  subtexto: string;
}

const DEFAULT_TEXTO = '¡Oferta especial! Diagnóstico electrónico GRATIS esta semana. Llama ahora y reserva tu cita.';
const DEFAULT_SUBTEXTO = 'Solo esta semana · Sin compromiso';

const AnuncioAdminTab = () => {
  const [data, setData] = useState<AnuncioData>({ activo: false, texto: DEFAULT_TEXTO, subtexto: DEFAULT_SUBTEXTO });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: row } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'anuncio')
        .maybeSingle();
      if (row?.value) setData(row.value as AnuncioData);
      setLoading(false);
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    await supabase
      .from('TALLERMECANICOKEVIN_settings')
      .upsert({ key: 'anuncio', value: data }, { onConflict: 'key' });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggleActivo = async (v: boolean) => {
    const next = { ...data, activo: v };
    setData(next);
    await supabase
      .from('TALLERMECANICOKEVIN_settings')
      .upsert({ key: 'anuncio', value: next }, { onConflict: 'key' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="ri-loader-4-line text-yellow-400 text-3xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-yellow-400/15 rounded-lg">
          <i className="ri-megaphone-line text-yellow-400 text-xl" />
        </div>
        <div>
          <h2 className="text-white font-black text-lg tracking-tight">Anuncio Flotante</h2>
          <p className="text-zinc-500 text-xs">Aparece en la esquina inferior derecha de la web cuando está activo</p>
        </div>
      </div>

      {/* Toggle activo */}
      <div className={`rounded-xl border p-5 flex items-center justify-between transition-all ${
        data.activo ? 'bg-yellow-400/8 border-yellow-400/40' : 'bg-zinc-900 border-zinc-800'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${data.activo ? 'bg-yellow-400 shadow-[0_0_8px_#facc15]' : 'bg-zinc-600'}`} />
          <div>
            <p className={`font-bold text-sm ${data.activo ? 'text-yellow-400' : 'text-zinc-300'}`}>
              {data.activo ? 'ANUNCIO ACTIVO — visible en la web ahora mismo' : 'Anuncio desactivado'}
            </p>
            <p className="text-zinc-500 text-xs mt-0.5">
              {data.activo ? 'Los visitantes verán el anuncio en la esquina inferior derecha' : 'Actívalo para que aparezca en la web'}
            </p>
          </div>
        </div>
        <button
          onClick={() => toggleActivo(!data.activo)}
          className={`relative w-12 h-6 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${
            data.activo ? 'bg-yellow-400' : 'bg-zinc-700'
          }`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
            data.activo ? 'left-6' : 'left-0.5'
          }`} />
        </button>
      </div>

      {/* Editor */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-5">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <i className="ri-edit-2-line text-yellow-400" />
          Contenido del anuncio
        </h3>

        {/* Texto principal */}
        <div className="space-y-2">
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">
            Texto principal
          </label>
          <textarea
            value={data.texto}
            onChange={(e) => setData({ ...data, texto: e.target.value })}
            rows={3}
            maxLength={200}
            placeholder="Ej: ¡Oferta especial! Diagnóstico GRATIS esta semana..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm placeholder-zinc-600 resize-none focus:outline-none focus:border-yellow-400/60 transition-colors"
          />
          <p className="text-zinc-600 text-xs text-right">{data.texto.length}/200 caracteres</p>
        </div>

        {/* Subtexto */}
        <div className="space-y-2">
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">
            Subtexto (debajo del texto principal)
          </label>
          <input
            type="text"
            value={data.subtexto}
            onChange={(e) => setData({ ...data, subtexto: e.target.value })}
            maxLength={80}
            placeholder="Ej: Solo esta semana · Sin compromiso"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-yellow-400/60 transition-colors"
          />
          <p className="text-zinc-600 text-xs text-right">{data.subtexto.length}/80 caracteres</p>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 font-bold text-sm text-black rounded-lg transition-all cursor-pointer whitespace-nowrap ${
            saved ? 'bg-green-400' : 'bg-yellow-400 hover:bg-yellow-300'
          }`}
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin" /> Guardando...</>
          ) : saved ? (
            <><i className="ri-check-double-line" /> ¡Guardado!</>
          ) : (
            <><i className="ri-save-line" /> Guardar cambios</>
          )}
        </button>
      </div>

      {/* Preview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <i className="ri-eye-line text-yellow-400" />
          Vista previa del anuncio
        </h3>
        <div className="flex justify-center">
          <div className="w-72 rounded-xl overflow-hidden shadow-2xl border-2 border-yellow-400/60 relative">
            {/* Racing stripe top */}
            <div className="h-2 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500" />
            {/* BG image */}
            <div
              className="relative h-40 bg-black flex items-center justify-center overflow-hidden"
              style={{
                backgroundImage: `url(https://public.readdy.ai/ai/img_res/fff308bd2e2f8e89a79e02fe9f1320e8.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
              {/* Checkered flag deco */}
              <div className="absolute top-2 left-2 flex gap-0.5 opacity-60">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className={`w-2 h-2 ${i % 2 === 0 ? 'bg-white' : 'bg-black border border-zinc-600'}`} />
                ))}
              </div>
              <div className="absolute top-2 right-8 flex gap-0.5 opacity-60">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className={`w-2 h-2 ${i % 2 === 0 ? 'bg-white' : 'bg-black border border-zinc-600'}`} />
                ))}
              </div>
              {/* Main text */}
              <div className="relative z-10 px-4 text-center">
                <p className="text-white font-black text-sm leading-tight drop-shadow-lg">
                  {data.texto || 'Escribe tu texto arriba...'}
                </p>
                {data.subtexto && (
                  <p className="text-yellow-400 text-xs font-bold mt-1.5 tracking-wide">{data.subtexto}</p>
                )}
              </div>
              {/* Mock close btn */}
              <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center border border-zinc-600">
                <i className="ri-close-line text-zinc-400 text-[10px]" />
              </div>
            </div>
            {/* Bottom bar */}
            <div className="h-1.5 bg-gradient-to-r from-yellow-500 via-yellow-300 to-yellow-500" />
          </div>
        </div>
        <p className="text-zinc-600 text-xs text-center">Así se verá el anuncio en la web (esquina inferior derecha)</p>
      </div>

    </div>
  );
};

export default AnuncioAdminTab;
